import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../api/hooks/useSmartAndRegularAssets";
import ExportButton from "../../../../../components/buttons/ExportButton";
import CustomToolTip from "../../../../../components/CustomTooltip";
import SearchInput from "../../../../../components/filters/SearchInput";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import { searchObjectProperties } from "../../../../../utils/search";
import EntityDrawer from "../../../environment/components/EntityDrawer/EntityDrawer";
import getEntitiesInAssetTableColumns from "./EntitiesInAssetTableColumnsView";

import { getPrettyDate, useLocalTime } from "../../../../../utils/timestamp";
export default function EntitiesInAssetTable({
  gridRef = undefined,
  entitiesInAsset,
  fetchedEntitiesAreLoading,
  handleTableExport,
  asset,
  searchString = "",
}) {
  const { trackEvent } = useAPI();
  const [selectedEntityForDrawer, setSelectedEntityForDrawer] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [entities, setEntities] = useState([]);
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });
  useEffect(() => {
    setAppliedFilters({ searchString: searchString });
  }, [searchString]);
  const localSearchParams = new URLSearchParams(location.search);
  const colors = useColors();
  const showLocalTime = useLocalTime();

  const [sortModel, setSortModel] = useState([
    { field: "risk", sort: "desc" },
    { field: "display_name", sort: "asc" },
  ]);
  const { regularAndActiveSmartAsset: assets } =
    useSmartAndRegularAssets(false);

  useEffect(() => {
    if (!fetchedEntitiesAreLoading) {
      setEntities(entitiesInAsset);
    }
  }, [fetchedEntitiesAreLoading, entitiesInAsset]);

  useEffect(() => {
    if (localSearchParams.has("entity")) {
      const entityId = decodeURIComponent(localSearchParams.get("entity"));
      if (!fetchedEntitiesAreLoading) {
        setSelectedEntityForDrawer(
          entities?.find((entity) => entity.id === entityId)
        );
        setIsDrawerOpen(true);
      }
    } else {
      setSelectedEntityForDrawer(null);
      setIsDrawerOpen(false);
    }
  }, [location, fetchedEntitiesAreLoading, entities]);

  useEffect(() => {
    if (!fetchedEntitiesAreLoading && selectedEntityForDrawer) {
      let updateSelectedEntity = entities?.find(
        (row) => row.id === selectedEntityForDrawer.id
      );
      setSelectedEntityForDrawer(updateSelectedEntity);
    }
  }, [entities, fetchedEntitiesAreLoading]);
  const onDrawerClose = () => {
    let searchParams = new URLSearchParams(location.search);
    searchParams.delete("entity");
    setSearchParams(searchParams.toString());
  };
  const onRowClick = (data) => {
    trackEvent(
      `User opened the drawer for entity ${data.row.display_name}, from the assets page`
    ).then();
    setSelectedEntityForDrawer(data.row);
    const params = new URLSearchParams(location.search);
    params.set("entity", encodeURIComponent(data.row.id));
    trackEvent(
      `Click on entity row with entity type ${data?.row?.entity_type} and entity id: ${data?.row?.entity_id}`
    );

    setSearchParams(params.toString());
    setIsDrawerOpen(true);
  };

  useEffect(() => {
    if (appliedFilters.searchString) {
      const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
      const filteredEntities = entitiesInAsset.filter((row) =>
        searchObjectProperties(row, lowerCaseSearchString)
      );
      setEntities(filteredEntities);
      trackEvent(
        `User searched for ${appliedFilters.searchString} in the entities table`
      );
    } else {
      setEntities(entitiesInAsset);
    }
  }, [appliedFilters.searchString, entitiesInAsset, searchString]);

  const newEntityInAsset = {
    field: "display_name",
    headerName: "Name",
    flex: 2,
    minWidth: 180,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    renderCell: (params) => {
      const newEntitiesById = Object.fromEntries(
        (asset?.new_entity_ids || []).map((entity) => [
          entity.entity_id,
          entity,
        ])
      );
      const foundEntity = newEntitiesById[params.row.entity_id];
      return (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="caption" sx={{ flexGrow: 1 }}>
            {params.value}
          </Typography>
          {foundEntity && (
            <CustomToolTip
              placement="top"
              title={
                foundEntity.first_seen.timestamp_utc
                  ? "Added on " +
                    getPrettyDate(
                      foundEntity.first_seen.timestamp_utc,
                      showLocalTime
                    )
                  : ""
              }
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.primary_4percent,
                  boxShadow: "0 0 1px 0 " + colors.primary_48percent,
                  borderRadius: "6px",
                  padding: "0px 4px",
                  gap: "5px",
                  height: "18px",
                  color: colors.primary_main,
                }}
              >
                <AutoAwesomeRoundedIcon
                  sx={{
                    color: colors.primary_main,
                    width: "16px",
                    height: "16px",
                  }}
                />
                <Typography variant="caption">Newly Added</Typography>
              </Box>
            </CustomToolTip>
          )}
        </Box>
      );
    },
  };
  const columns = getEntitiesInAssetTableColumns(assets, colors);
  // Replace the existing display_name column with an enhanced version that shows new entity indicators
  const existingColumnIndex = columns.findIndex(
    (col) => col.field === newEntityInAsset.field
  );
  if (existingColumnIndex !== -1) {
    columns[existingColumnIndex] = newEntityInAsset;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            width: "300px",
          }}
        >
          <SearchInput
            fieldName={"searchString"}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            placeholder="Search entities"
          />
        </Box>
        <ExportButton
          sx={{
            border: "1px solid " + colors.grey_48percent,
          }}
          handleExport={handleTableExport}
        />
      </Box>

      <StyledDataGrid
        columns={columns}
        rows={entities}
        boxSXProps={{
          cursor: "pointer",
          "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
            {
              paddingLeft: "24px",
            },
          "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
            {
              paddingRight: "24px",
            },
        }}
        loading={fetchedEntitiesAreLoading}
        onRowClick={onRowClick}
        pageSizeOptionsOverride={[10]}
        disableRowSelectionOnClick
        apiRef={gridRef}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
      />
      {selectedEntityForDrawer && (
        <EntityDrawer
          openDrawer={isDrawerOpen}
          selectedEntity={selectedEntityForDrawer}
          onClose={() => {
            setIsDrawerOpen(false);
            onDrawerClose();
            setTimeout(() => {
              setSelectedEntityForDrawer(null);
            }, 226);
          }}
        />
      )}
    </Box>
  );
}
